import React, { useEffect, useState, useRef } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Swal from 'sweetalert2';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { generateAxiosInstanceInstitute } from '../hooks/useAuthorizedRequestForInstitute';
import { generateAxiosInstanceCommonFileUpload } from '../hooks/useAuthorizedRequestForFileUpload';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../components/ModalsCommonFunctions'; import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import Pagination from '../components/Pagination';
import Select from 'react-select';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CModal } from '@coreui/react';
import { subscriptionFormValidation } from '../validation/Validation';
import { ZodError } from 'zod';
import { ErrorMessage } from '../validation/validationError';
import DraggableMenu from '../components/DraggableMenu';

const SubscriptionMaster = () => {
    const authorizedAxios = generateAxiosInstance();
    const authorizedAxiosCommonFileUpload = generateAxiosInstanceCommonFileUpload();
    const draggingItemRef = useRef(null);
    const [initialMenu, setInitialMenu] = useState([]);
    const [isDragged, setIsDragged] = useState(false);
    const initialState = {
        subscriptionName: '',
        details: '',
        amount: '',
        menu: [],
        color: ''
    }

    const [subscriptionData, setSubscriptionData] = useState(initialState);
    const [subscriptionList, setSubscriptionList] = useState([]);
    const [menuList, setMenuList] = useState([]);
    const [menuDetails, setMenuDetails] = useState([]);

    const [menuIdsEdit, setMenuIdsEdit] = useState([]);

    const [visible, setVisible] = useState(false);

    const [editId, setEditId] = useState(null);
    const [errors, setErrors] = useState([]);

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);

    const [subscriptionSort, setSubscriptionSort] = useState(null);
    const [amountSort, setAmountSort] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["subscriptionName", "amount"];

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        getSubscriptionList();
        getMenuList();
    }, []);

    const getSubscriptionList = async () => {
        const apiUrl = `/subscription`;
        try {
            const response = await authorizedAxios.get(apiUrl);
            setSubscriptionList(response.data.data);
            return response.data.data;
        } catch (error) {
            setSubscriptionList([]);
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            console.error('Error fetching data:', errorMsg);
        }
    }

    const getMenuList = async () => {
        const apiUrl = '/get-menu';
        try {
            const response = await authorizedAxios.get(apiUrl);
            const menuOptions = response.data.result.map((menu) => ({
                value: menu.id,
                label: menu.menuName,
            }));
            setMenuList(menuOptions);
        } catch (error) {
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            console.error('Error fetching data:', errorMsg);
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSubscriptionData(prevData => {
            if (name === 'amount') {
                const filteredValue = value.replace(/[^0-9]/g, '');
                return {
                    ...prevData,
                    [name]: filteredValue
                }
            } else {
                return {
                    ...prevData,
                    [name]: value
                }
            }
        });

        if (value.trim().length !== 0) {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== name));
        }
    }

    const handleMenuChange = (selectedOptions) => {
        const selectedIds = selectedOptions.map((option) => option.value.toString());
        setSubscriptionData((prevData) => {
            return {
                ...prevData,
                menu: selectedIds,
            }
        })

        if (selectedIds.length !== 0) {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== 'menu'));
        }
    }

    const handleDetailChange = (editor) => {
        let value = editor.getData();
        setSubscriptionData((prevData) => {
            return {
                ...prevData,
                details: value
            }
        })

        if (value.trim().length !== 0) {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== 'details'));
        }
    }

    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            background: subscriptionData.menu.includes(state.value.toString()) ? 'lightpink' : 'white',
        }),
    }

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            getSubscriptionList();
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            setSubscriptionList(await searchArray("A", `/subscription`, searchKeys, searchValue.trim(), 'data'))
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(subscriptionList, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId])

    useEffect(() => {
        setTotalPages(Math.ceil(subscriptionList.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > subscriptionList.length) {
            endIndex = subscriptionList.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(subscriptionList.slice(startIndex, endIndex));
    }, [subscriptionList, currPage, itemsPerPage]);


    const addSubscription = async () => {
        const apiUrl = `/subscription`;
        try {
            await subscriptionFormValidation.parseAsync(subscriptionData);
            setErrors([]);
            setLoading(true);
            const response = await authorizedAxios.post(apiUrl, subscriptionData);

            await getSubscriptionList();
            setSaveId(response.data.data.id);
            setSubscriptionData(initialState);
            setEditId(null);
            // setSubscriptionName('')
            // setAmount('')

            // setTagSort(null);
            // setSubscriptionSort(null);
            // setAmountSort(null);
            // setSearchValue(null);
            Swal.fire(
                { text: response.data.message }
            );
        } catch (err) {
            if (err instanceof ZodError) {
                setErrors(err.errors);
            } else {
                const errorMsg = err?.response?.data?.message || 'Something went wrong!';
                Swal.fire({ text: errorMsg });
            }
        } finally {
            setLoading(false);
        }
    }

    const deleteMenu = async (subscriptionId, id) => {
        let confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (confirmed) {
            try {
                setLoading(true);
                const apiUrl = `/delete-menu-from-subscription?id=${id}`;
                const response = await authorizedAxios.delete(apiUrl);
                const page = deleteItem(currPage, itemsPerPage, subscriptionList.length);
                const updatedSubscriptionList = await getSubscriptionList();

                const updatedMenu = updatedSubscriptionList.find(item => item.id == subscriptionId);
                setMenuDetails({ subscriptionId: updatedMenu.id, subscriptionMenuMapping: updatedMenu.subscriptionMenuMapping });
                setInitialMenu(updatedMenu.subscriptionMenuMapping);
                setCurrPage(page);
                Swal.fire(
                    { text: response.data.message }
                );
            } catch (error) {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            } finally {
                setLoading(false);
            }
        }
    }

    function uploadAdapter(loader) {
        return {
            upload: () => {
                return new Promise((resolve, reject) => {
                    const body = new FormData();
                    loader.file.then(async (file) => {
                        body.append("file", file);
                        const apiUrl = "uploadimage";
                        authorizedAxiosCommonFileUpload && authorizedAxiosCommonFileUpload.post(apiUrl, body)
                            .then((res) => {
                                resolve({ default: res.data.result });
                            })
                            .catch((err) => {
                                reject(err.response.data.message);
                            });
                    });
                });
            },
        };
    }

    function uploadPlugin(editor) {
        editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
            return uploadAdapter(loader);
        };
    }

    const arraysHaveCommonValues = (array1, array2) => {
        let commonValuesFound = false;
        for (const value of array1) {
            if (array2.includes(value)) {
                commonValuesFound = true;
                break;
            }
        }
        return commonValuesFound;
    }

    const handleChangeEdit = (selectedOptions) => {
        const selectedIds = selectedOptions.map((option) => option.value.toString());
        !arraysHaveCommonValues(selectedIds, subscriptionData.menu) && setMenuIdsEdit(selectedIds);
    }

    const updateSubscription = async () => {
        try {
            setLoading(true);
            const apiUrl = `/edit-subscription-master?id=${editId}`;
            const response = await authorizedAxios.put(apiUrl, { ...subscriptionData, menu: menuIdsEdit });
            await getSubscriptionList();
            setEditId(null);
            setSubscriptionData(initialState);
            setMenuIdsEdit([]);
            Swal.fire(
                { text: response.data.message }
            );
        } catch (error) {
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            Swal.fire(
                { text: errorMsg }
            );
        } finally {
            setLoading(false);
        }
    }


    useEffect(() => {
        if (subscriptionSort == true || subscriptionSort == false) {
            setAmountSort(null);
        }
    }, [subscriptionSort])

    useEffect(() => {
        if (amountSort == true || amountSort == false) {
            setSubscriptionSort(null);
        }
    }, [amountSort])

    const handleInputReset = () => {
        setSubscriptionData(initialState);
        setEditId(null);
        setErrors([]);
        setSubscriptionSort(null);
        setAmountSort(null);
        setSearchValue(null);
    }

    const handleDragStart = (e, index) => {
        draggingItemRef.current = index;
        e.currentTarget.classList.add("dragging");
    };

    const handleDragEnd = (e) => {
        e.currentTarget.classList.remove("dragging");
        draggingItemRef.current = null;

        const initialSequence = initialMenu.map(item => item.id);
        const updatedSequence = menuDetails.subscriptionMenuMapping.map(item => item.id);
        const isSame = updatedSequence === initialSequence;

        setIsDragged(!isSame);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();

        const draggingIndex = draggingItemRef.current;

        if (draggingIndex === index) return;

        const updatedItems = [...menuDetails.subscriptionMenuMapping];

        // Swap sequenceIds
        const draggedItem = updatedItems[draggingIndex];
        const targetItem = updatedItems[index];

        const tempSequenceId = draggedItem.sequenceId;
        draggedItem.sequenceId = targetItem.sequenceId;
        targetItem.sequenceId = tempSequenceId;

        // Move draggedItem to the new index
        updatedItems.splice(draggingIndex, 1);
        updatedItems.splice(index, 0, draggedItem);

        // Update reference
        draggingItemRef.current = index;

        setMenuDetails((prev) => ({
            ...prev,
            subscriptionMenuMapping: updatedItems,
        }));
    };

    const updateMenuSequence = async () => {
        try {
            setLoading(true);
            const updatedMenu = menuDetails.subscriptionMenuMapping.map(item => ({id: item.id, sequenceId: item.sequenceId }));
            const apiUrl = `/update-menu-sequence`;
            const response = await authorizedAxios.patch(apiUrl, updatedMenu);
            await getSubscriptionList();
            setIsDragged(false);
            setVisible(false);
            setMenuDetails({});
            setInitialMenu([]);
            Swal.fire(
                { text: response.data.message }
            );
        } catch (error) {
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            Swal.fire(
                { text: errorMsg }
            );
        } finally {
            setLoading(false);
        }
    }


    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <div id='scroll-up'></div>
                <Header />
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb my-0 ms-2">
                        <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                            <span>Dashboard</span>
                        </li>
                        <li className="breadcrumb-item active"><span>Subscription Master</span></li>
                    </ol>
                </nav>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fa-solid fa-tag"></i>Subscription Master</b></h4>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">

                                                <div className="col-md-4">
                                                    <label for="subscriptionName" className="col-form-label"><span style={{ color: "red" }}>*</span>Subscription Name</label>
                                                    <input type="text" className="form-control" id="subscriptionName" name='subscriptionName' value={subscriptionData.subscriptionName} onChange={handleInputChange} />
                                                    <ErrorMessage errors={errors} field="subscriptionName" />
                                                </div>

                                                <div className="col-md-4">
                                                    <label for="amount" className="col-form-label"><span style={{ color: "red" }}>*</span>Amount</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">₹</span>
                                                        <input type="text" className="form-control" id="amount" name='amount' min="0" value={subscriptionData.amount} onChange={handleInputChange} />
                                                    </div>
                                                    <ErrorMessage errors={errors} field="amount" />
                                                </div>

                                                {editId ?
                                                    <div className="col-md-4">
                                                        <label for="menu" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Menu Edit : {subscriptionData.menu.length} selected</label>
                                                        <Select
                                                            value={menuList.filter(option =>
                                                                menuIdsEdit && menuIdsEdit.includes(option.value.toString())
                                                            )}
                                                            isMulti
                                                            name="menu"
                                                            options={menuList}
                                                            className="basic-multi-select"
                                                            classNamePrefix="Select Menu"
                                                            onChange={handleChangeEdit}
                                                            styles={customStyles}
                                                        />
                                                    </div>
                                                    :
                                                    <div className="col-md-4">
                                                        <label for="menu" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Menu:</label>
                                                        <Select
                                                            value={menuList.filter(option =>
                                                                subscriptionData.menu && subscriptionData.menu.includes(option.value.toString())
                                                            )}
                                                            isMulti
                                                            name="menu"
                                                            options={menuList}
                                                            className="basic-multi-select"
                                                            classNamePrefix="Select Menu"
                                                            onChange={handleMenuChange}
                                                        />
                                                        <ErrorMessage errors={errors} field="menu" />
                                                    </div>}

                                                <div className="col-md-4">
                                                    <label htmlFor="color" className="col-form-label"><span style={{ color: "red" }}>*</span>Choose Color</label>
                                                    <input type="color" className="form-control form-control-color" id="color" name="color" value={subscriptionData.color} onChange={handleInputChange} title="Choose your color" />
                                                    <ErrorMessage errors={errors} field="color" />
                                                </div>

                                                <div className="col-md-12">
                                                    <label for="inputPassword" name="details" className="col-form-label"><span style={{ color: "red" }}>*</span>Sub Topic Details :</label>
                                                    <CKEditor
                                                        editor={ClassicEditor}
                                                        data={subscriptionData.details}
                                                        onReady={editor => {
                                                            // You can store the "editor" and use when it is needed.
                                                            // console.log('Editor is ready to use!', editor);
                                                        }}
                                                        onChange={(event, editor) => {
                                                            // console.log(event)
                                                            handleDetailChange(editor)
                                                        }}

                                                        config={{
                                                            extraPlugins: [uploadPlugin],
                                                        }}

                                                        onInit={(editor) => {
                                                            // Register the custom upload adapter
                                                            uploadPlugin(editor);
                                                        }}
                                                    />
                                                    <ErrorMessage errors={errors} field="details" />
                                                </div>

                                                <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>
                                                    <div className="col-auto new_button">
                                                        {!editId && <button className="btn btn-primary" style={{ marginRight: '20px' }} type="button" onClick={addSubscription}>Save</button>}
                                                        <button className="btn btn-danger delete_icon delete_icon " type="button" style={{ marginRight: "20px" }} onClick={handleInputReset}>Reset</button>
                                                        {
                                                            editId && (<button className="btn btn-success approve_icon" type="button" onClick={updateSubscription} disabled={!editId}>Update</button>)
                                                        }
                                                    </div>
                                                </div>

                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>
                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col">Sr. No.</th>
                                                                        <th scope="col">
                                                                            Subscription Name
                                                                            <SortIcon sortState={subscriptionSort} setSortState={setSubscriptionSort} setData={setSubscriptionList} setCurrPage={setCurrPage} sortingPath={"subscriptionName"} />
                                                                        </th>
                                                                        <th scope="col">
                                                                            Amount
                                                                            <SortIcon sortState={amountSort} setSortState={setAmountSort} setData={setSubscriptionList} setCurrPage={setCurrPage} sortingPath={"amount"} />
                                                                        </th>
                                                                        <th scope="col">Subscription Details</th>
                                                                        <th scope='col'>Menu</th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        displayedData.length > 0 ?
                                                                            displayedData.map((sub, index) => {
                                                                                const menuIds = sub.subscriptionMenuMapping.map(menu => menu.menuMasterDetails.id.toString());
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row">{startIndex + index + 1}</th>
                                                                                        <td>{sub?.subscriptionName}</td>
                                                                                        <td>{sub?.amount}</td>
                                                                                        <td>
                                                                                            <button className="btn btn-primary view_icon" type="button" onClick={() => {
                                                                                                setMenuDetails(sub?.details);
                                                                                                setVisible(1)
                                                                                            }}><i class="fa fa-eye" ></i></button>
                                                                                        </td>
                                                                                        <td>
                                                                                            <button className="btn btn-primary view_icon" type="button" disabled={editId == sub.id} onClick={() => {
                                                                                                setMenuDetails({ subscriptionId: sub.id, subscriptionMenuMapping: sub?.subscriptionMenuMapping });
                                                                                                setInitialMenu(sub?.subscriptionMenuMapping);
                                                                                                setVisible(2);
                                                                                            }}><i class="fa fa-eye" ></i></button>
                                                                                        </td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-warning edit_icon" type="button" /*disabled={editId == sub.id}*/ onClick={() => {
                                                                                                setEditId(sub.id);
                                                                                                setSubscriptionData({
                                                                                                    subscriptionName: sub.subscriptionName,
                                                                                                    details: sub.details,
                                                                                                    amount: sub.amount,
                                                                                                    menu: menuIds,
                                                                                                    color: sub.color
                                                                                                })
                                                                                                document.getElementById('scroll-up').scrollIntoView({ behavior: 'smooth' });
                                                                                            }}><i class="fa-light fa-pen-to-square"></i></button>
                                                                                            {/* <button className="btn btn-outline-danger delete_icon" type="button" disabled={editId === sub.id} onClick={() => deleteCourse(sub.id)}> <i class="fa-light fa-trash-can"></i></button> */}
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center' }}>No Subscription Available</th>
                                                                            </tr>)
                                                                    }
                                                                </tbody>
                                                            </table>
                                                            <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={subscriptionList.length} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            <CModal
                backdrop="static"
                visible={visible}
                onClose={() => setVisible(false)}
                aria-labelledby="LiveDemoExampleLabel"
            >
                {visible === 1 ? (
                    // For `sub?.details` (HTML content)
                    <div className="book book-modal">
                        <div className="table-header-like">Subscription Details</div>
                        <div
                            className="page"
                            dangerouslySetInnerHTML={{
                                __html: `<style>.ckContent img{max-width:100% !important; height: auto !important}</style><div class="ck-content">${menuDetails}</div>`,
                            }}
                        ></div>
                        <button
                            className="btn btn-outline-warning edit_icon"
                            type="button"
                            onClick={() => setVisible(false)}
                        >
                            Close
                        </button>
                    </div>
                ) : visible === 2 ? (
                    // <div className="book book-modal p-4">
                    //     <div className="table-header-like fs-4 fw-bold mb-3 text-center">Menu Details</div>
                    //     <div className="page">
                    //         <ul className="list-group list-group-flush shadow-sm rounded border">
                    //             {menuDetails?.subscriptionMenuMapping?.map((item, index) => (
                    //                 <>
                    //                     <li key={index} className="list-group-item d-flex align-items-center justify-content-between">
                    //                         <div className="d-flex align-items-center">
                    //                             <i className="fa fa-check-circle text-success me-2"></i>
                    //                             <span className="fw-medium">{item.menuMasterDetails?.menuName || 'Unnamed Menu'}</span>
                    //                         </div>
                    //                         <button className="btn btn-outline-danger delete_icon" type="button" /*disabled={editId === sub.id}*/ onClick={() => deleteMenu(menuDetails.subscriptionId, item.id)}> <i class="fa-light fa-trash-can"></i></button>
                    //                     </li>
                    //                 </>
                    //             ))}
                    //         </ul>
                    //     </div>
                    //     <div className="text-end mt-4">
                    //         <button
                    //             className="btn btn-outline-warning"
                    //             type="button"
                    //             onClick={() => setVisible(false)}
                    //         >
                    //             Close
                    //         </button>
                    //     </div>
                    // </div>
                    // <DraggableMenu />

                    <div className="book book-modal p-4">
                        <div className="table-header-like fs-4 fw-bold mb-3 text-center">Menu Details</div>
                        <div className="page">
                            <ul className="list-group list-group-flush shadow-sm rounded border">
                                {menuDetails?.subscriptionMenuMapping?.map((item, index) => (
                                    <li
                                        key={item.id}
                                        className="list-group-item d-flex align-items-center justify-content-between"
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleDragOver(e, index, item.sequenceId)}
                                        style={{
                                            backgroundColor: draggingItemRef.current === index ? "#f0f0f0" : "",
                                            opacity: draggingItemRef.current === index ? 0.7 : 1,
                                            cursor: "move",
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <span
                                                className="me-3 d-inline-flex justify-content-center align-items-center rounded-circle bg-primary text-white fw-bold"
                                                style={{ width: "30px", height: "30px", fontSize: "14px" }}
                                            >
                                                {index + 1}
                                            </span>
                                            <i className="fa fa-check-circle text-success me-2"></i>
                                            <span className="fw-medium">{item.menuMasterDetails?.menuName || "Unnamed Menu"}</span>
                                        </div>
                                        <button
                                            className="btn btn-outline-danger delete_icon"
                                            type="button"
                                            onClick={() => deleteMenu(menuDetails.subscriptionId, item.id)}
                                        >
                                            <i className="fa-light fa-trash-can"></i>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="text-end mt-4">
                            {isDragged && <button className="btn btn-outline-success me-2" type="button" onClick={() => { updateMenuSequence() }}>
                                Save
                            </button>}
                            <button className="btn btn-outline-warning" type="button" onClick={() => { setVisible(false); setIsDragged(false); setMenuDetails({}); setInitialMenu([]) }}>
                                Close
                            </button>
                        </div>
                    </div>
                ) : null}
            </CModal>

        </>
    )
};

export default SubscriptionMaster;